import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSafeState, useUnmount, useUnmountedRef } from 'ahooks';
import { storagePrefixV2 } from '@/constant';

import cx from './index.module.scss';

interface Props {
  level: AIDiagnose.PainLevel;
  imgTop?: number;
}

const COLOR_LEVEL_CONFIG: Record<
  AIDiagnose.PainLevel,
  {
    colorHex: string;
    bgUrl: string;
  }
> = {
  1: {
    colorHex: '#c0e5ea',
    bgUrl: storagePrefixV2 + '/ai-consulation/level-1-bg-min.png',
  },
  2: {
    colorHex: '#d4e4b6',
    bgUrl: storagePrefixV2 + '/ai-consulation/level-2-bg-min.png',
  },
  3: {
    colorHex: '#e5cfb5',
    bgUrl: storagePrefixV2 + '/ai-consulation/level-3-bg-min.png',
  },
  4: {
    colorHex: '#e8c9b7',
    bgUrl: storagePrefixV2 + '/ai-consulation/level-4-bg-min.png',
  },
  5: {
    colorHex: '#e7b8b7',
    bgUrl: storagePrefixV2 + '/ai-consulation/level-5-bg-min.png',
  },
};

export const ActiveBgColor: React.FC<Props> = ({ level, imgTop }) => {
  const [renderConfig, setRenderConfig] = useState<{
    prevLevel: AIDiagnose.PainLevel;
    currentLevel: AIDiagnose.PainLevel;
    renderIndex: number;
  }>({
    prevLevel: null,
    currentLevel: null,
    renderIndex: 0,
  });

  const [isImg1Play, setIsImg1Play] = useSafeState(false);
  const [isImg2Play, setIsImg2Play] = useSafeState(false);
  const timerListRef = useRef<NodeJS.Timeout[]>([]);
  const [shouldPlayAnimation, setShouldPlayAnimation] = useSafeState(false);
  const isUnmount = useUnmountedRef();

  const bg1Color = useMemo(
    () =>
      COLOR_LEVEL_CONFIG[renderConfig?.[renderConfig?.renderIndex % 2 === 0 ? 'currentLevel' : 'prevLevel']]?.colorHex,
    [renderConfig]
  );
  const bg2Color = useMemo(
    () =>
      COLOR_LEVEL_CONFIG[renderConfig?.[renderConfig?.renderIndex % 2 !== 0 ? 'currentLevel' : 'prevLevel']]?.colorHex,
    [renderConfig]
  );

  // 控制背景图片循环效果
  const loopControlPlay = useCallback(() => {
    if (isUnmount.current) return;

    setIsImg1Play(true);
    timerListRef.current.push(setTimeout(() => setIsImg1Play(false), 8000));
    timerListRef.current.push(
      setTimeout(() => {
        setIsImg2Play(true);

        timerListRef.current.push(setTimeout(() => loopControlPlay(), 7000));
        timerListRef.current.push(setTimeout(() => setIsImg2Play(false), 8000));
      }, 7000)
    );
  }, [isUnmount, setIsImg1Play, setIsImg2Play]);

  useUnmount(() => {
    timerListRef.current?.forEach(timer => clearTimeout(timer));
  });

  useEffect(() => {
    setRenderConfig(prev => ({
      ...prev,
      // level值改变时，将当前正在渲染的的 level 存储至 prevLevel 中，并将传入的最新的 level 存储至 currentLevel 中
      ...(!prev?.currentLevel ? { currentLevel: level } : { prevLevel: prev.currentLevel, currentLevel: level }),
      renderIndex: prev.renderIndex + 1,
    }));

    setShouldPlayAnimation(!!level);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level]);

  useEffect(() => {
    shouldPlayAnimation && loopControlPlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPlayAnimation]);

  return (
    <div className={cx('ActiveBgColor')}>
      <div
        style={{ '--prev-bg-color': bg1Color } as any}
        className={cx('bg-item', 'prev', { show: bg1Color && renderConfig?.renderIndex % 2 === 0 })}
      />
      <div
        style={{ '--current-bg-color': bg2Color } as any}
        className={cx('bg-item', 'current', { show: bg2Color && renderConfig?.renderIndex % 2 !== 0 })}
      />
      <div className={cx('img-bg-wrapper')} style={imgTop ? { top: `${(imgTop / 7.8).toFixed(4)}vw` } : {}}>
        {renderConfig?.currentLevel && (
          <>
            <div className={cx('img-bg-item', 'img-bg-item-1', { play: isImg1Play })}>
              <img src={storagePrefixV2 + '/ai-consulation/wave-bg.png'} alt="" />
            </div>
            <div className={cx('img-bg-item', 'img-bg-item-2', { play: isImg2Play })}>
              <img src={storagePrefixV2 + '/ai-consulation/wave-bg.png'} alt="" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
